import { clsx } from 'clsx';
import { FC, ReactNode } from 'react';

import { Breakpoint, unstable_composeClasses as composeClasses } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';

import Box, { BoxProps } from '@o2/ui/Box';
import UiContainer from '@o2/ui/Container';

import Column from './Column';
import containerClasses, { getContainerUtilityClass } from './containerClasses';
import {
  ColumnsVariant,
  OneColColumnsVariant,
  TwoColColumnsVariant,
  gap,
  singleColumnLayouts,
  singleColumnMaxWidths,
  wideScreenBreakpointMap,
  xMargin,
  yMargin,
} from './values';

export interface ContainerProps extends BoxProps {
  children: ReactNode;
  variant: ColumnsVariant;
  smallScreenReverse?: boolean;
  disableGrid?: boolean;

  /**
   * Disable both horizontal (column) and vertical (row) gap.
   */
  disableGap?: boolean;

  disableMarginX?: boolean;
  disableMarginY?: boolean;
  rootMaxWidth?: false | string;
}

type ContainerOwnerState = Omit<ContainerProps, keyof BoxProps | 'children'>;

/**
 * Get maximum permissible column width, or 100% if undefined.
 */
const getMaxColumnWidth = (variant: ColumnsVariant) => {
  if (variant in singleColumnMaxWidths) {
    return singleColumnMaxWidths[variant as OneColColumnsVariant] ?? '100%';
  } else {
    return '100%';
  }
};

const getSpacingsAndWidthForBreakpoint = (
  theme: Theme,
  breakpoint: Breakpoint,
  variant: ColumnsVariant,
  disableGrid?: boolean,
  disableGap?: boolean,
  disableMarginX?: boolean,
  disableMarginY?: boolean
) => {
  return {
    ...(!disableGrid && {
      gap: theme.spacing(disableGap ? 0 : gap[breakpoint]),
      margin: theme.spacing(disableMarginY ? 0 : yMargin[breakpoint], disableMarginX ? 0 : xMargin[breakpoint]),
    }),
    '& > *': {
      maxWidth: `min(${getMaxColumnWidth(variant)}, calc(100vw - 2 * ${theme.spacing(
        !disableMarginX ? xMargin[breakpoint] : 0
      )}))`,
      minWidth: 0,
    },
  };
};

const useUtilityClasses = (classes: ContainerProps['classes']) => {
  const slots = {
    root: ['root'],
    columns: ['columns'],
  };

  const composedClasses = composeClasses(slots, getContainerUtilityClass, classes);

  return {
    ...classes,
    ...composedClasses,
  };
};

const ColumnsRoot = styled(Box, {
  name: 'O2Container',
  slot: 'Columns',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop),
})<{ ownerState: ContainerOwnerState }>(
  ({
    theme,
    ownerState: { disableGrid, disableGap, disableMarginX, disableMarginY, smallScreenReverse, variant },
  }) => ({
    display: 'grid',

    [theme.breakpoints.up('xs')]: {
      ...getSpacingsAndWidthForBreakpoint(
        theme,
        'xs',
        variant,
        disableGrid,
        disableGap,
        disableMarginX,
        disableMarginY
      ),
    },

    [theme.breakpoints.up('sm')]: {
      ...getSpacingsAndWidthForBreakpoint(
        theme,
        'sm',
        variant,
        disableGrid,
        disableGap,
        disableMarginX,
        disableMarginY
      ),
    },

    [theme.breakpoints.up('md')]: {
      ...getSpacingsAndWidthForBreakpoint(
        theme,
        'md',
        variant,
        disableGrid,
        disableGap,
        disableMarginX,
        disableMarginY
      ),
      ...(variant === 'half' && {
        gridTemplateColumns: 'repeat(2, 1fr)',
        '& > *:nth-of-type(1)': {
          gridColumn: '1 / span 1',
        },
        '& > *:nth-of-type(2)': {
          gridColumn: '2 / span 1',
        },
      }),
    },

    [theme.breakpoints.up('lg')]: {
      ...getSpacingsAndWidthForBreakpoint(
        theme,
        'lg',
        variant,
        disableGrid,
        disableGap,
        disableMarginX,
        disableMarginY
      ),
      ...(variant === 'narrow-wide' && {
        gridTemplateColumns: 'repeat(3, 1fr)',
        '& > *:nth-of-type(1)': {
          gridColumn: '1 / span 1',
        },
        '& > *:nth-of-type(2)': {
          gridColumn: '2 / span 2',
        },
      }),
      ...(variant === 'stretch-narrow' && {
        gridTemplateColumns: 'auto 1fr',
        '& > *:nth-of-type(1)': {
          gridColumn: '1 / span 1',
        },
        '& > *:nth-of-type(2)': {
          gridColumn: '2 / span 1',
        },
      }),
      ...(variant === 'wide-narrow' && {
        gridTemplateColumns: 'repeat(3, 1fr)',
        '& > *:nth-of-type(1)': {
          gridColumn: '1 / span 2',
        },
        '& > *:nth-of-type(2)': {
          gridColumn: '3 / span 1',
        },
      }),
    },

    [theme.breakpoints.up('xl')]: {
      ...getSpacingsAndWidthForBreakpoint(
        theme,
        'xl',
        variant,
        disableGrid,
        disableGap,
        disableMarginX,
        disableMarginY
      ),
    },

    ...((singleColumnLayouts as ColumnsVariant[]).includes(variant)
      ? {
          // only 1 col, always
          gridTemplateColumns: '1fr',
          justifyItems: 'center',
          '& > *:nth-of-type(1)': {
            gridColumn: '1 / span 1',
            width: '100%',
          },
        }
      : {
          [theme.breakpoints.down(wideScreenBreakpointMap[variant as TwoColColumnsVariant])]: {
            // screen small enough -> change to rows instead of cols
            gridTemplateColumns: '1fr',
            '& > *': {
              gridColumn: '1 / span 1',
            },

            ...(smallScreenReverse && {
              '& > *:nth-of-type(1)': {
                order: 2,
              },
              '& > *:nth-of-type(2)': {
                order: 1,
              },
            }),
          },
        }),
  })
);

const StyledUiContainer = styled(UiContainer)<{ ownerState: { rootMaxWidth: ContainerProps['rootMaxWidth'] } }>(
  ({ theme, ownerState: { rootMaxWidth } }) => ({
    maxWidth: !rootMaxWidth ? '1920px' : rootMaxWidth,

    // TODO: get rid of !important
    padding: '0 !important',

    // make the gap between containers the same size as the gap between columns
    [theme.breakpoints.only('xs')]: {
      [`& ~ .${containerClasses.root}`]: {
        marginTop: theme.spacing(gap['xs']),
      },
    },
  })
);

type Container = FC<ContainerProps> & {
  Column: typeof Column;
};

/**
 * Page content container.
 *
 * If using a multi-column layout, it is necessary to supply `Container.Column` children! In case of single-column layouts
 * (such as 'full-wide'...), the sole column is inserted automatically (i.e. there is no need for you to use it in your code).
 */
const Container: Container = ({
  children,
  classes,
  disableGrid = false,
  disableGap = false,
  disableMarginX = false,
  disableMarginY = false,
  smallScreenReverse = false,
  variant,
  rootMaxWidth = false,
  sx,
  ...props
}) => {
  const wrap = (singleColumnLayouts as ColumnsVariant[]).includes(variant);
  const utilityClasses = useUtilityClasses(classes);

  // TODO: UiContainer doesn't accept exact widths, only breakpoints
  return (
    <StyledUiContainer maxWidth={false} ownerState={{ rootMaxWidth }} className={clsx(utilityClasses.root)}>
      <ColumnsRoot
        ownerState={{
          disableGrid,
          disableGap,
          disableMarginX,
          disableMarginY,
          smallScreenReverse,
          variant,
        }}
        className={clsx(utilityClasses.columns)}
        sx={sx}
        {...props}
      >
        {wrap ? <Column disableGap={disableGap}>{children}</Column> : children}
      </ColumnsRoot>
    </StyledUiContainer>
  );
};

Container.Column = Column;

export default Container;
